import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "../lib/api";
import { Badge, Button, Card, EmptyState, Field, Input, Modal, PageHeader, Select, Spinner, Table, Textarea } from "../components/ui";
import { TEMPLATE_CATEGORY_LABELS, fmtDateTime } from "../lib/format";

type Template = { id: string; name: string; category: string; subject: string; bodyText: string; bodyHtml: string | null; fromName: string | null; tags: string[]; isActive: boolean; updatedAt: string };
const EMPTY = { name: "", category: "custom", subject: "", bodyText: "", bodyHtml: "", fromName: "", replyTo: "", tags: "" };

export function Templates() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ subject: string; text: string; html: string | null } | null>(null);
  const { data, isLoading } = useQuery({
    queryKey: ["templates", search],
    queryFn: () => api<{ items: Template[] }>(`/templates?search=${encodeURIComponent(search)}`),
  });
  const save = useMutation({
    mutationFn: (payload: any) =>
      editId ? api(`/templates/${editId}`, { method: "PATCH", body: JSON.stringify(payload) }) : api("/templates", { method: "POST", body: JSON.stringify(payload) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["templates"] }); setModal(false); setForm(EMPTY); setEditId(null); },
  });
  const del = useMutation({ mutationFn: (id: string) => api(`/templates/${id}`, { method: "DELETE", body: "{}" }), onSuccess: () => qc.invalidateQueries({ queryKey: ["templates"] }) });
  const clone = useMutation({ mutationFn: (id: string) => api(`/templates/${id}/clone`, { method: "POST", body: "{}" }), onSuccess: () => qc.invalidateQueries({ queryKey: ["templates"] }) });

  return (
    <div>
      <PageHeader
        title="Email Templates"
        subtitle="Maintain a library of test messages for placement tests"
        actions={<Button onClick={() => { setForm(EMPTY); setEditId(null); setModal(true); }}>+ New Template</Button>}
      />
      <Card className="p-0">
        <div className="border-b border-ink-100 p-4"><Input placeholder="Search templates…" value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-xs" /></div>
        {isLoading ? <Spinner /> : !data?.items.length ? (
          <div className="p-6"><EmptyState title="No templates" description="Create templates with variables like {{firstName}} and {{companyName}}." /></div>
        ) : (
          <Table headers={["Name", "Category", "Subject", "Tags", "Active", "Updated", "Actions"]}>
            {data!.items.map((t) => (
              <tr key={t.id} className="table-row-hover">
                <td className="px-4 py-3 font-medium text-ink-800">{t.name}</td>
                <td className="px-4 py-3"><Badge tone="neutral">{TEMPLATE_CATEGORY_LABELS[t.category] ?? t.category}</Badge></td>
                <td className="max-w-[260px] truncate px-4 py-3 text-ink-600">{t.subject || "—"}</td>
                <td className="px-4 py-3"><div className="flex max-w-[180px] flex-wrap gap-1">{t.tags.map((tag) => <Badge key={tag} tone="brand">{tag}</Badge>)}</div></td>
                <td className="px-4 py-3">{t.isActive ? <Badge tone="success">Active</Badge> : <Badge tone="neutral">Disabled</Badge>}</td>
                <td className="px-4 py-3 text-xs text-ink-500">{fmtDateTime(t.updatedAt)}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    <button className="rounded px-2 py-1 text-xs text-brand-600 hover:bg-brand-50" onClick={() => { setForm({ ...EMPTY, ...t, fromName: t.fromName ?? "", bodyHtml: t.bodyHtml ?? "", tags: t.tags.join(","), replyTo: "" }); setEditId(t.id); setModal(true); }}>Edit</button>
                    <button className="rounded px-2 py-1 text-xs text-ink-500 hover:bg-ink-50" onClick={() => setPreview({ subject: t.subject, text: t.bodyText, html: t.bodyHtml })}>Preview</button>
                    <button className="rounded px-2 py-1 text-xs text-ink-500 hover:bg-ink-50" onClick={() => clone.mutate(t.id)}>Clone</button>
                    <button className="rounded px-2 py-1 text-xs text-red-600 hover:bg-red-50" onClick={() => { if (confirm(`Delete ${t.name}?`)) del.mutate(t.id); }}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      <Modal open={modal} onClose={() => setModal(false)} title={editId ? "Edit Template" : "New Template"} wide>
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Template Name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
            <Field label="Category">
              <Select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                {Object.entries(TEMPLATE_CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </Select>
            </Field>
            <Field label="From Name"><Input value={form.fromName} onChange={(e) => setForm({ ...form, fromName: e.target.value })} /></Field>
            <Field label="Reply-To"><Input value={form.replyTo} onChange={(e) => setForm({ ...form, replyTo: e.target.value })} /></Field>
            <Field label="Tags (comma separated)"><Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="quarterly, newsletter" /></Field>
          </div>
          <Field label="Subject"><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} /></Field>
          <Field label="Plain Text Body"><Textarea rows={6} value={form.bodyText} onChange={(e) => setForm({ ...form, bodyText: e.target.value })} placeholder="Hi {{firstName}},…" /></Field>
          <Field label="HTML Body" hint="Optional — supports {{firstName}}, {{companyName}}, {{senderName}} and more">
            <Textarea rows={8} value={form.bodyHtml} onChange={(e) => setForm({ ...form, bodyHtml: e.target.value })} className="font-mono text-xs" placeholder="<p>Hi {{firstName}},</p>…" />
          </Field>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setModal(false)}>Cancel</Button>
            <Button onClick={() => save.mutate({ ...form, tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean), bodyHtml: form.bodyHtml || null })} disabled={save.isPending}>Save template</Button>
          </div>
        </div>
      </Modal>

      <Modal open={Boolean(preview)} onClose={() => setPreview(null)} title="Template Preview" wide>
        {preview && (
          <div className="space-y-4">
            <div><div className="text-xs font-medium uppercase text-ink-400">Subject</div><div className="mt-1 rounded-lg bg-ink-50 px-3 py-2 text-sm">{preview.subject}</div></div>
            <div><div className="text-xs font-medium uppercase text-ink-400">Plain text</div><pre className="mt-1 max-h-60 overflow-auto whitespace-pre-wrap rounded-lg bg-ink-50 px-3 py-2 text-xs">{preview.text}</pre></div>
            {preview.html && <div><div className="text-xs font-medium uppercase text-ink-400">HTML</div><div className="mt-1 max-h-72 overflow-auto rounded-lg border border-ink-200 p-4" dangerouslySetInnerHTML={{ __html: preview.html }} /></div>}
          </div>
        )}
      </Modal>
    </div>
  );
}
